import { computed } from 'vue';

import { type AgentFragmentFragment, useAgentLoggedQuery } from '@/graphql/generated/graphql';
import userHasAtLeastOneRight, { type UserRightFormat } from '@/util/rights/userHasAtLeastOneRight';

const hasRight = (roles: string | string[], userRight?: UserRightFormat[] | null) => {
  if (userRight && userRight.length > 0) {
    if (typeof roles === 'string') {
      return userHasAtLeastOneRight([roles], userRight);
    }
    return userHasAtLeastOneRight(roles, userRight);
  }
  return false;
};

/**
 * Return agent from URQL cache. If it's use outside current session it can fail.
 */
const useAgent = () => {
  const { data } = useAgentLoggedQuery({});
  const agent = computed<AgentFragmentFragment>(
    () => data.value?.agentLogged as AgentFragmentFragment,
  );
  const allianzUserRights = () => hasRight('AGAUSER', agent.value.permissions);
  const rentersRights = () => hasRight('RIMPLMTN', agent.value.permissions);
  const implementationRights = () => hasRight('IMPLMTN', agent.value.permissions);
  const tuitionRights = () => hasRight('TIMPLMTN', agent.value.permissions);
  const schoolRights = () => hasRight(['TIMPLMTN', 'RIMPLMTN'], agent.value.permissions);
  const boosmanRights = () => hasRight('BOSSMAN', agent.value.permissions);
  const caseManagerRights = () => hasRight('CASEMNGR', agent.value.permissions);
  const caseAgentRights = () => hasRight('CASES', agent.value.permissions);
  const sendyRights = () => hasRight('SENDY', agent.value.permissions);
  const sendyPanelRights = () => hasRight('SENDYPNL', agent.value.permissions);
  const superUserRights = () => hasRight('SUPERUSR', agent.value.permissions);
  const ppAdminRights = () => hasRight('PPADMIN', agent.value.permissions);
  const bulkImplementationRolloverRights = () => hasRight('BULKIMPL', agent.value.permissions);
  const schoolSuccessRights = () => hasRight('SCHOLSUC', agent.value.permissions);
  const agencyManagementRights = () => hasRight('AGMNGMT', agent.value?.permissions);
  const agencyInternRights = () => hasRight('AGINTERN', agent.value.permissions);
  const editNotesRights = () => hasRight('EDITNTS', agent.value.permissions);
  const tuitionMissingTransactionsRights = () => hasRight('TMISSING', agent.value.permissions);
  const fileImportRights = () => hasRight('FILEIMPT', agent.value.permissions);
  const licensedRights = () => hasRight('LICENSED', agent.value.permissions);
  return {
    agent,
    allianzUserRights,
    rentersRights,
    implementationRights,
    tuitionRights,
    schoolRights,
    boosmanRights,
    caseManagerRights,
    caseAgentRights,
    sendyRights,
    sendyPanelRights,
    superUserRights,
    ppAdminRights,
    bulkImplementationRolloverRights,
    schoolSuccessRights,
    agencyManagementRights,
    agencyInternRights,
    editNotesRights,
    tuitionMissingTransactionsRights,
    fileImportRights,
    licensedRights,
  };
};

export default useAgent;
